function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import { jsx } from 'panel';
import { ContextPanelReactProps } from 'common/widgets/context-panel';
import { EntityType, SharableEntityType } from 'common/report/util/saved-entities/types';
import { promptSharing } from 'common/widgets/sharing';
import { getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { ActionType, ContextPanelData, DetailType } from 'common/widgets/context-panel/types';
import { ModelStatus } from 'common/util/model-resource/model-resource';
import { getEditCapability, getShareCapability } from 'common/report/util/saved-entities';
import { BehaviorsResource } from 'common/report/model-resources/behaviors';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { Translatable } from 'common/i18n';
import { BehaviorDefinitionPanelEl, Props as BehaviorDefinitionPanelProps } from 'iron/common/widgets/context-panel/behavior-definition-panel';
import { rgbaStringFromHexString } from 'common/util/colors';
import { BehaviorTypeIcons, getBaseColorFromMetricType, OldMetricTypeIcons } from 'common/report/insights/util/metrics';
import type { ContextPanelRendererParams } from '../child-menu';

/**
 * Provides handlers for events inside a metric context panel
 */
export const createContextPanelEventHandlers = function createContextPanelEventHandlers(): Partial<ContextPanelReactProps> {
  let params: Partial<ContextPanelRendererParams> = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const behaviorId = ev.detail.item.id;
    if (action === ActionType.ShareBehavior) {
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: behaviorId,
        'project-id': Number(getProjectId())
      });
    } else if (ev.detail.item.url && action === ActionType.ViewEntity) {
      window.open(ev.detail.item.url, "_blank");
    } else {
      throw new Error("action handler not implemented");
    }
  };
  const handleContextPanelEditDescription = async (_ref: CustomEvent) => {
    let {
      detail
    }: CustomEvent = _ref;
    const behaviorId = detail.item.id;
    const updatedDescription = detail.description;
    const behaviorsCtx = params.availableContexts.behaviors;
    try {
      await behaviorsCtx.patch(behaviorId, {
        description: updatedDescription
      });
    } catch (_unused) {
      addToast({
        message: "Sorry, something went wrong. Please contact support if this problem persists.",
        type: ToastType.ERROR,
        timeout: null
      });
    }
  };
  return {
    onHoverIn: params.handleHoverIn,
    onHoverOut: params.handleHoverOut,
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription
  };
};

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateBehaviorContextPanelData(_ref2: {
  behaviorsCtx: BehaviorsResource;
  canShare?: boolean;
  canWriteBehaviors?: boolean;
  firstClassMetricsEnabled?: boolean;
  id: number;
}): ContextPanelData {
  var _behavior$last_verifi, _behavior$last_verifi2;
  let {
    behaviorsCtx,
    canShare = false,
    canWriteBehaviors = false,
    firstClassMetricsEnabled,
    id
  }: {
    behaviorsCtx: BehaviorsResource;
    canShare?: boolean;
    canWriteBehaviors?: boolean;
    firstClassMetricsEnabled?: boolean;
    id: number;
  } = _ref2;
  const model = behaviorsCtx.getOne(id);
  const behavior = model.value;
  if (model.status === ModelStatus.Loading || !behavior) {
    return null;
  }
  const permissions = getEditCapability(behavior);
  const actions: ContextPanelData[`actions`] = [];
  const links: ContextPanelData[`links`] = [];
  const details: ContextPanelData[`details`] = [];

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: ActionType.ShareBehavior,
      label: _ref3 => {
        let {
          t
        } = _ref3;
        return t("query-builder.context-menu.share");
      },
      icon: "invite",
      // Only superadmin or editor can share
      disabled: !getShareCapability(behavior, EntityType.Behavior).enableShare,
      disabledTooltipText: _ref4 => {
        let {
          t
        } = _ref4;
        return t("query-builder.context-menu.share-no-access");
      },
      item: {
        id: behavior.id,
        superadminOverrideShare: getShareCapability(behavior).requireSuperadminOverride
      }
    });
  }

  // view entity in lexicon link
  links.push({
    icon: "share",
    href: getReportUrl("lexicon", {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId()
    }) + "/transformations/behaviors/".concat(behavior.id),
    tooltipText: _ref5 => {
      let {
        t
      } = _ref5;
      return t("query-builder.context-menu.view-in-lexicon");
    }
  });
  actions.push({
    name: ActionType.ViewEntity,
    label: _ref6 => {
      let {
        t
      } = _ref6;
      return t("query-builder.context-menu.view-in-lexicon");
    },
    icon: "share",
    item: {
      id: behavior.id,
      name: behavior.name,
      url: getReportUrl("lexicon", {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId()
      }) + "/transformations/behaviors/".concat(behavior.id)
    }
  });
  if (behavior.created_by) {
    details.push({
      name: _ref7 => {
        let {
          t
        } = _ref7;
        return t("query-builder.context-menu.creator");
      },
      type: DetailType.User,
      value: _objectSpread({}, behavior.created_by)
    });
  }
  const headers: Record<string, Translatable> = {
    funnel: _ref8 => {
      let {
        t
      } = _ref8;
      return t("query-builder.metrics.funnel");
    },
    retention: _ref9 => {
      let {
        t
      } = _ref9;
      return t("query-builder.metrics.retention");
    }
  };
  const iconMap = firstClassMetricsEnabled ? BehaviorTypeIcons : OldMetricTypeIcons;
  const icon = iconMap[behavior.type];
  let definitionRenderer: ContextPanelData[`definitionRenderer`];
  if (firstClassMetricsEnabled) {
    definitionRenderer = () => {
      return jsx(BehaviorDefinitionPanelEl["panelTagName"] as string, {
        params: {
          props: {
            behavior: behavior.definition.behavior,
            excludeBehaviorName: true
          } satisfies BehaviorDefinitionPanelProps
        }
      });
    };
  }
  return {
    actions,
    links,
    backgroundColor: firstClassMetricsEnabled ? rgbaStringFromHexString(getBaseColorFromMetricType(behavior.definition.behavior.type), 0.04) : undefined,
    header: behavior.name,
    subHeader: headers[behavior.type],
    entityIcon: icon,
    description: behavior.description,
    verified: behavior.verified,
    verifiedBy: ((_behavior$last_verifi = behavior.last_verified_by) === null || _behavior$last_verifi === void 0 ? void 0 : _behavior$last_verifi.name) || ((_behavior$last_verifi2 = behavior.last_verified_by) === null || _behavior$last_verifi2 === void 0 ? void 0 : _behavior$last_verifi2.email),
    verifiedAt: behavior.last_verified,
    entityType: EntityType.Behavior,
    excludedMetricIcons: ["pie", "pulse"],
    definitionRenderer,
    details,
    // allows for editing the description of the metric
    editItem: canWriteBehaviors && permissions.enableEdit ? {
      id: behavior.id,
      type: EntityType.Behavior,
      name: behavior.name
    } : null
  };
}